import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Badge } from '../components/ui/Badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/Table';
import { Select } from '../components/ui/Select';
import { Search, Filter, Trash2, BarChart3, Calendar, Users, Shield } from 'lucide-react';
import { api } from '../lib/api';
import { toast } from 'react-hot-toast';

interface LogAcesso {
  id: string;
  ip: string;
  user: string;
  dataHora: string;
  validacao: boolean;
  createdAt: string;
}

interface LogStats {
  total: number;
  success: number;
  failed: number;
  successRate: number;
  recentLogs: number;
  topUsers: Array<{
    user: string;
    _count: { user: number };
  }>;
  dailyStats: Array<{
    date: string;
    total: number;
    success: number;
    failed: number;
  }>;
}

interface LogFilters {
  user?: string;
  validacao?: boolean;
  startDate?: string;
  endDate?: string;
  limit?: number;
  offset?: number;
}

export default function LogAcessoPage() {
  const [logs, setLogs] = useState<LogAcesso[]>([]);
  const [stats, setStats] = useState<LogStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [_statsLoading, setStatsLoading] = useState(true);
  const [filters, setFilters] = useState<LogFilters>({
    limit: 100,
    offset: 0
  });
  const [totalLogs, setTotalLogs] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);

  const loadLogs = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      
      if (filters.user) params.append('user', filters.user);
      if (filters.validacao !== undefined) params.append('validacao', filters.validacao.toString());
      if (filters.startDate) params.append('startDate', filters.startDate);
      if (filters.endDate) params.append('endDate', filters.endDate);
      if (filters.limit) params.append('limit', filters.limit.toString());
      if (filters.offset) params.append('offset', filters.offset.toString());

      const response = await api.get(`/log-acesso/logs?${params}`);
      
      if (response.data.success) {
        setLogs(response.data.data.logs);
        setTotalLogs(response.data.data.total);
      }
    } catch (error) {
      console.error('Erro ao carregar logs:', error);
      toast.error('Erro ao carregar logs de acesso');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      setStatsLoading(true);
      const response = await api.get('/log-acesso/logs/stats');
      
      if (response.data.success) {
        setStats(response.data.data);
      }
    } catch (error) {
      console.error('Erro ao carregar estatísticas:', error);
      toast.error('Erro ao carregar estatísticas');
    } finally {
      setStatsLoading(false);
    }
  };

  const cleanOldLogs = async () => {
    if (!confirm('Tem certeza que deseja limpar os logs antigos (mais de 90 dias)?')) {
      return;
    }

    try {
      const response = await api.delete('/log-acesso/logs/clean');
      
      if (response.data.success) {
        toast.success(response.data.message);
        loadLogs();
        loadStats();
      }
    } catch (error) {
      console.error('Erro ao limpar logs:', error);
      toast.error('Erro ao limpar logs antigos');
    }
  };

  const handleFilterChange = (key: keyof LogFilters, value: any) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleSearch = () => {
    setCurrentPage(1);
    setFilters(prev => ({ ...prev, offset: 0 }));
  };

  const handlePageChange = (page: number) => {
    const offset = (page - 1) * (filters.limit || 100);
    setCurrentPage(page);
    setFilters(prev => ({ ...prev, offset }));
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('pt-BR');
  };

  const getTotalPages = () => {
    return Math.ceil(totalLogs / (filters.limit || 100));
  };

  useEffect(() => {
    loadLogs();
    loadStats();
  }, [filters.offset]);

  useEffect(() => {
    loadLogs();
  }, [filters.offset]);

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold">Logs de Acesso</h1>
        <Button onClick={cleanOldLogs} variant="destructive" size="sm">
          <Trash2 className="w-4 h-4 mr-2" />
          Limpar Logs Antigos
        </Button>
      </div>

      {/* Estatísticas */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center space-x-2">
                <BarChart3 className="w-5 h-5 text-blue-500" />
                <div>
                  <p className="text-sm text-muted-foreground">Total de Logs</p>
                  <p className="text-2xl font-bold">{stats.total.toLocaleString()}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center space-x-2">
                <Shield className="w-5 h-5 text-green-500" />
                <div>
                  <p className="text-sm text-muted-foreground">Acessos Bem-sucedidos</p>
                  <p className="text-2xl font-bold">{stats.success.toLocaleString()}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center space-x-2">
                <Shield className="w-5 h-5 text-red-500" />
                <div>
                  <p className="text-sm text-muted-foreground">Acessos Mal-sucedidos</p>
                  <p className="text-2xl font-bold">{stats.failed.toLocaleString()}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center space-x-2">
                <Calendar className="w-5 h-5 text-purple-500" />
                <div>
                  <p className="text-sm text-muted-foreground">Taxa de Sucesso</p>
                  <p className="text-2xl font-bold">{stats.successRate.toFixed(1)}%</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filtros */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Filter className="w-5 h-5 mr-2" />
            Filtros
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="text-sm font-medium">Usuário</label>
              <Input
                placeholder="Filtrar por usuário"
                value={filters.user || ''}
                onChange={(e) => handleFilterChange('user', e.target.value)}
              />
            </div>

            <div>
              <label className="text-sm font-medium">Status</label>
              <Select
                value={filters.validacao?.toString() || ''}
                onValueChange={(value) => handleFilterChange('validacao', value === 'true' ? true : value === 'false' ? false : undefined)}
                placeholder="Todos"
              >
                <option value="">Todos</option>
                <option value="true">Bem-sucedido</option>
                <option value="false">Mal-sucedido</option>
              </Select>
            </div>

            <div>
              <label className="text-sm font-medium">Data Início</label>
              <Input
                type="date"
                value={filters.startDate || ''}
                onChange={(e) => handleFilterChange('startDate', e.target.value)}
              />
            </div>

            <div>
              <label className="text-sm font-medium">Data Fim</label>
              <Input
                type="date"
                value={filters.endDate || ''}
                onChange={(e) => handleFilterChange('endDate', e.target.value)}
              />
            </div>
          </div>

          <div className="flex justify-end mt-4">
            <Button onClick={handleSearch}>
              <Search className="w-4 h-4 mr-2" />
              Buscar
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Tabela de Logs */}
      <Card>
        <CardHeader>
          <CardTitle>Logs de Acesso ({totalLogs.toLocaleString()})</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">Carregando...</div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Data/Hora</TableHead>
                    <TableHead>Usuário</TableHead>
                    <TableHead>IP</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {logs.map((log) => (
                    <TableRow key={log.id}>
                      <TableCell>{formatDate(log.dataHora)}</TableCell>
                      <TableCell className="font-medium">{log.user}</TableCell>
                      <TableCell className="font-mono text-sm">{log.ip}</TableCell>
                      <TableCell>
                        <Badge variant={log.validacao ? "default" : "destructive"}>
                          {log.validacao ? "Bem-sucedido" : "Mal-sucedido"}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Paginação */}
              {getTotalPages() > 1 && (
                <div className="flex justify-center mt-4 space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    Anterior
                  </Button>
                  
                  <span className="flex items-center px-3">
                    Página {currentPage} de {getTotalPages()}
                  </span>
                  
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === getTotalPages()}
                  >
                    Próxima
                  </Button>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Top Usuários */}
      {stats?.topUsers && stats.topUsers.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <Users className="w-5 h-5 mr-2" />
              Top Usuários
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {stats.topUsers.map((user, _index) => (
                <div key={user.user} className="flex justify-between items-center p-2 bg-muted rounded">
                  <span className="font-medium">{user.user}</span>
                  <Badge variant="secondary">{user._count.user} tentativas</Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
